import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, CalendarDays, Clock, Package, X, Upload } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { getMyOrders, createOrder, getMyBookings, getOrdersConfig, uploadOrderImage } from "../lib/customerApi";

const STAGES = ["Submitted", "Received", "Cleaning/Prep", "Submitted to Grader", "Grading", "Returned/Ready", "Consigned", "Sold"];
const REQ_STYLE = { pending: "bg-gold text-black", approved: "bg-green-600 text-white", declined: "bg-red-900/60 text-red-300" };

function Stepper({ status }) {
  const idx = Math.max(0, STAGES.indexOf(status));
  return (
    <div className="mt-4 flex flex-wrap gap-x-1 gap-y-2">
      {STAGES.map((s, i) => (
        <div key={s} className="flex items-center gap-1">
          <span className={`h-2 w-2 rounded-full ${i <= idx ? "bg-gold" : "bg-[#3a3a3a]"}`} />
          <span className={`font-mono text-[10px] uppercase tracking-[0.08em] ${i === idx ? "text-gold" : i < idx ? "text-zinc-400" : "text-zinc-600"}`}>{s}</span>
          {i < STAGES.length - 1 && <span className="w-3 h-px bg-[#2a2a2a] mx-0.5" />}
        </div>
      ))}
    </div>
  );
}

export default function AccountPortal() {
  const { user, ready, logout } = useCustomerAuth();
  const [orders, setOrders] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [config, setConfig] = useState({ services: [] });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ service: "", card_description: "", quantity: 1, note: "", images: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!ready || !user) return;
    getMyOrders().then(setOrders).catch(() => setOrders([]));
    getMyBookings().then(setBookings).catch(() => setBookings([]));
    getOrdersConfig().then((c) => { setConfig(c); setForm((f) => ({ ...f, service: c.services[0] || "" })); }).catch(() => {});
  }, [ready, user]);

  if (!ready) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-gold" /></div>;
  if (!user) return <Navigate to="/account/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service || !form.card_description) { toast.error("Service and card description are required."); return; }
    setSaving(true);
    try {
      const created = await createOrder(form);
      setOrders((arr) => [created, ...(arr || [])]);
      setShowForm(false);
      setForm({ service: config.services[0] || "", card_description: "", quantity: 1, note: "", images: [] });
      toast.success("Submission received! Track its progress below.");
    } catch { toast.error("Could not submit. Please try again."); }
    finally { setSaving(false); }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        if (!f.type.startsWith("image/")) { toast.error("Only image files are allowed."); continue; }
        urls.push(await uploadOrderImage(f));
      }
      setForm((fm) => ({ ...fm, images: [...fm.images, ...urls] }));
      if (urls.length) toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} attached.`);
    } catch { toast.error("Upload failed. Please try again."); }
    finally { setUploading(false); }
  };
  const removeImage = (url) => setForm((fm) => ({ ...fm, images: fm.images.filter((u) => u !== url) }));

  const input = "w-full bg-transparent border-b border-[#3a3a3a] focus:border-gold py-2.5 text-white placeholder:text-zinc-600 outline-none transition-colors text-sm";

  return (
    <main className="min-h-screen pt-24 pb-24 px-5 sm:px-8" data-testid="account-portal">
      <div className="max-w-[1100px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">[ My Account ]</span>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tighter mt-2">
              Hi, {user.name || "there"}
            </h1>
            <p className="text-zinc-500 text-sm mt-1 font-mono">{user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="font-mono text-xs uppercase tracking-[0.12em] text-zinc-400 hover:text-white">View Site</Link>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-[#3a3a3a] hover:border-gold hover:text-gold px-5 py-2.5 font-mono text-xs uppercase tracking-[0.12em] transition-colors" data-testid="account-logout">
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        {/* Orders */}
        <section className="mb-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl font-semibold inline-flex items-center gap-2"><Package size={20} className="text-gold" /> Card Service Orders</h2>
            <button onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.12em] px-5 py-2.5 font-semibold hover:bg-[#d59e19] transition-colors" data-testid="new-order-btn">
              {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Cancel" : "New Submission"}
            </button>
          </div>

          {showForm && (
            <form onSubmit={submit} className="border border-[#27272a] bg-[#121212] p-6 mb-6 grid sm:grid-cols-2 gap-x-8 gap-y-5" data-testid="order-form">
              <div>
                <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Service</label>
                <select className={`${input} cursor-pointer`} value={form.service} onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))} data-testid="order-service">
                  {config.services.map((s) => <option key={s} value={s} className="bg-[#121212]">{s}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Quantity</label>
                <input type="number" min="1" className={input} value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: Number(e.target.value) }))} data-testid="order-quantity" />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Card(s) description</label>
                <input className={input} placeholder="e.g. 2023-24 Bellingham Pink Refractor /250" value={form.card_description} onChange={(e) => setForm((f) => ({ ...f, card_description: e.target.value }))} data-testid="order-description" />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Notes (optional)</label>
                <textarea rows={2} className={`${input} resize-none`} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="order-note" />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Card photos (optional)</label>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  {form.images.map((url) => (
                    <div key={url} className="relative h-20 w-20 border border-[#3a3a3a] overflow-hidden group" data-testid="order-image-preview">
                      <img src={url} alt="card" className="h-full w-full object-cover" />
                      <button type="button" onClick={() => removeImage(url)} className="absolute top-0.5 right-0.5 h-5 w-5 grid place-items-center rounded-full bg-black/70 text-white hover:bg-red-600 transition-colors" aria-label="Remove photo" data-testid="order-image-remove">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  <label className="h-20 w-20 grid place-items-center border border-dashed border-[#3a3a3a] hover:border-gold hover:text-gold text-zinc-500 cursor-pointer transition-colors" data-testid="order-image-upload">
                    {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={uploading} data-testid="order-image-input" />
                  </label>
                </div>
                <p className="text-zinc-600 text-[11px] mt-1.5 font-mono">Attach photos of your card(s) so we know exactly what's coming in.</p>
              </div>
              <button type="submit" disabled={saving || uploading} className="sm:col-span-2 justify-self-start inline-flex items-center gap-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 font-semibold hover:bg-[#d59e19] transition-colors disabled:opacity-60" data-testid="order-submit">
                {saving ? <Loader2 size={15} className="animate-spin" /> : "Submit For Service"}
              </button>
            </form>
          )}

          {!orders ? <Loader2 className="animate-spin text-gold" /> : !orders.length ? (
            <div className="border border-[#27272a] p-8 text-center text-zinc-500 text-sm" data-testid="no-orders">No submissions yet. Send us your first card above.</div>
          ) : (
            <div className="space-y-4" data-testid="orders-list">
              {orders.map((o) => (
                <div key={o.id} className="border border-[#27272a] p-6" data-testid={`order-${o.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-lg font-semibold">{o.service}</h3>
                      <p className="text-zinc-400 text-sm mt-1">{o.card_description} {o.quantity > 1 && <span className="text-zinc-600">· x{o.quantity}</span>}</p>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] bg-gold text-black px-2.5 py-1">{o.status}</span>
                  </div>
                  {o.images?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2" data-testid={`order-images-${o.id}`}>
                      {o.images.map((url) => (
                        <a key={url} href={url} target="_blank" rel="noreferrer" className="h-16 w-16 border border-[#2a2a2a] overflow-hidden hover:border-gold transition-colors">
                          <img src={url} alt="card" className="h-full w-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  <Stepper status={o.status} />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Bookings */}
        <section>
          <h2 className="font-display text-2xl font-semibold inline-flex items-center gap-2 mb-6"><CalendarDays size={20} className="text-gold" /> Studio Bookings</h2>
          {!bookings ? <Loader2 className="animate-spin text-gold" /> : !bookings.length ? (
            <div className="border border-[#27272a] p-8 text-center text-zinc-500 text-sm" data-testid="no-bookings">
              No studio bookings yet. <Link to="/book" className="text-gold hover:underline">Book a session →</Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="bookings-list">
              {bookings.map((b) => (
                <div key={b.id} className="border border-[#27272a] p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`booking-${b.id}`}>
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 font-display text-lg font-semibold"><CalendarDays size={16} className="text-gold" /> {new Date(b.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</span>
                    <span className="inline-flex items-center gap-1.5 font-mono text-sm text-zinc-300"><Clock size={13} className="text-gold" /> {b.start_time}</span>
                    <span className="font-mono text-xs text-zinc-400">{b.setup}</span>
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 ${REQ_STYLE[b.status] || "bg-[#27272a] text-zinc-300"}`}>{b.status}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
